import React, { useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe';
import PropTypes from 'prop-types';

/**
 * Stripe Elements Provider Wrapper
 *
 * Provides Stripe context to child components and configures
 * the appearance of Stripe Elements to match the app's design.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.clientSecret - Payment Intent client secret
 * @param {Object} [props.appearance] - Custom appearance configuration
 */
export function StripeProvider({ children, clientSecret, appearance }) {
  // Get Stripe instance (memoized singleton)
  const stripePromise = useMemo(() => getStripe(), []);

  // Configure Elements options
  const options = useMemo(() => {
    if (!clientSecret) return null;

    return {
      clientSecret,
      appearance: appearance || {
        theme: 'stripe',
        variables: {
          colorPrimary: '#0066cc',
          colorBackground: '#ffffff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px',
          fontSizeBase: '16px',
        },
        rules: {
          '.Input': {
            border: '1px solid #e0e0e0',
            boxShadow: 'none',
            padding: '12px',
          },
          '.Input:focus': {
            border: '1px solid #0066cc',
            boxShadow: '0 0 0 1px #0066cc',
          },
          '.Input--invalid': {
            border: '1px solid #df1b41',
          },
          '.Label': {
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '8px',
          },
        },
      },
    };
  }, [clientSecret, appearance]);

  // Show loading state if no client secret yet
  if (!clientSecret) {
    return (
      <div className="stripe-provider-loading">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3"></div>
          <p className="text-gray-600">Initializing secure payment...</p>
        </div>
      </div>
    );
  }

  // Show error if Stripe couldn't be loaded
  if (!stripePromise) {
    return (
      <div className="stripe-provider-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <svg
            className="w-12 h-12 text-red-600 mx-auto mb-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z"
              clipRule="evenodd"
            />
          </svg>
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Payment System Unavailable
          </h3>
          <p className="text-red-700">
            Unable to load payment system. Please refresh the page or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
}

StripeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  clientSecret: PropTypes.string.isRequired,
  appearance: PropTypes.object,
};

export default StripeProvider;
